import { useState, useEffect, useMemo } from 'react';
import { axiosInstance } from '../services/api';

// API base URL for constructing full media URLs
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Prefix relative URLs with the API base URL
 */
function getFullMediaUrl(relativeUrl) {
  if (!relativeUrl) return null;
  // If already a full URL, return as-is
  if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
    return relativeUrl;
  }
  // Prefix with base URL
  return `${API_BASE_URL}${relativeUrl}`;
}

/**
 * Family Board Data Hook
 *
 * Fetches and transforms data for the Family Board from the dashboard API.
 * Formats data for WaitingSection, RecentSection, and StatsBar.
 */
export function useFamilyBoardData(activeFamilyId = null, families = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Stable key so effect doesn't re-run on every render
  const familyKey = useMemo(
    () => families.map(f => f.id).sort().join(','),
    [families]
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (families.length > 1) {
          // Cross-family mode: fetch from every family in parallel
          const responses = await Promise.all(
            families.map(f =>
              axiosInstance.get('/dashboard/', {
                headers: { 'X-Family-Id': f.id }
              })
            )
          );

          // Merge stories from all families, deduplicating by story ID
          const allStories = [];
          const seenStoryIds = new Set();
          const allConnections = [];
          const seenConnectionIds = new Set();

          responses.forEach((response, idx) => {
            const familyName = families[idx].name;
            const familyId = families[idx].id;
            const dashData = response.data;

            (dashData.stories || []).forEach(story => {
              if (!seenStoryIds.has(story.id)) {
                seenStoryIds.add(story.id);
                allStories.push({ ...story, familyName, familyId });
              }
            });

            (dashData.connections || []).forEach(conn => {
              if (!seenConnectionIds.has(conn.id)) {
                seenConnectionIds.add(conn.id);
                allConnections.push(conn);
              }
            });
          });

          // Sort stories newest-first
          allStories.sort((a, b) => new Date(b.sharedAt || 0) - new Date(a.sharedAt || 0));

          // Use first response as base for user info
          const baseData = responses[0].data;
          const mergedData = {
            ...baseData,
            stories: allStories,
            connections: allConnections,
            stats: {
              ...baseData.stats,
              storyCount: allStories.length,
              connectionCount: allConnections.length,
            },
          };

          const transformedData = transformForFamilyBoard(mergedData);
          setData(transformedData);
        } else {
          // Single-family mode (original behaviour)
          const response = await axiosInstance.get('/dashboard/');
          const dashboardData = response.data;
          const transformedData = transformForFamilyBoard(dashboardData);
          setData(transformedData);
        }
      } catch (err) {
        console.error('Error fetching family board data:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeFamilyId, familyKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = async () => {
    setLoading(true);
    setError(null);
    try {
      if (families.length > 1) {
        const responses = await Promise.all(
          families.map(f =>
            axiosInstance.get('/dashboard/', {
              headers: { 'X-Family-Id': f.id }
            })
          )
        );
        const allStories = [];
        const seenStoryIds = new Set();
        const allConnections = [];
        const seenConnectionIds = new Set();

        responses.forEach((response, idx) => {
          const familyName = families[idx].name;
          const familyId = families[idx].id;
          const dashData = response.data;
          (dashData.stories || []).forEach(story => {
            if (!seenStoryIds.has(story.id)) {
              seenStoryIds.add(story.id);
              allStories.push({ ...story, familyName, familyId });
            }
          });
          (dashData.connections || []).forEach(conn => {
            if (!seenConnectionIds.has(conn.id)) {
              seenConnectionIds.add(conn.id);
              allConnections.push(conn);
            }
          });
        });
        allStories.sort((a, b) => new Date(b.sharedAt || 0) - new Date(a.sharedAt || 0));
        const baseData = responses[0].data;
        const mergedData = {
          ...baseData,
          stories: allStories,
          connections: allConnections,
          stats: { ...baseData.stats, storyCount: allStories.length, connectionCount: allConnections.length },
        };
        setData(transformForFamilyBoard(mergedData));
      } else {
        const response = await axiosInstance.get('/dashboard/');
        setData(transformForFamilyBoard(response.data));
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, refetch };
}

/**
 * Transform dashboard API data into Family Board format
 */
function transformForFamilyBoard(dashboardData) {
  const { user, stats, connections, stories, questions } = dashboardData;

  // Transform questions into waitingItems format
  const waitingItems = transformQuestionsToWaitingItems(questions || [], connections || []);

  // Transform stories into recentMoments format (user's own stories)
  const recentMoments = transformStoriesToMoments(stories || [], user);

  // Transform stories and questions into Wisdom Threads format
  const wisdomThreads = transformToWisdomThreads(stories || [], questions || [], user, connections || []);

  // TODO: Fetch actual time capsules when backend supports it
  const upcomingCapsules = [];

  // Normalize stories with full media URLs for new layout
  const normalizedStories = (stories || []).map(story => ({
    ...story,
    thumbnailUrl: getFullMediaUrl(story.thumbnailUrl),
    videoDirectUrl: getFullMediaUrl(story.videoDirectUrl),
  }));

  return {
    user: {
      id: user?.id || null,
      firstName: user?.firstName || 'User',
      lastName: user?.lastName || '',
      avatarInitial: user?.avatarInitial || 'U',
      avatarColor: user?.avatarColor || '#6B7F4A',
      storyCount: user?.storyCount || 0,
    },
    stats: {
      waitingCount: waitingItems.length,
      storyCount: stats?.storyCount || 0,
      familyMemberCount: stats?.connectionCount || 0,
      daysUntilCapsule: null // TODO: Get from time capsule API
    },
    stories: normalizedStories,
    connections: connections || [],
    waitingItems,
    recentMoments,
    upcomingCapsules,
    wisdomThreads
  };
}

/**
 * Transform stories and questions into Wisdom Threads format
 * Maps to the 6 thread types: photo_voice, video_wisdom, then_now, artifact, memory_layers, time_capsule
 */
function transformToWisdomThreads(stories, questions, user, connections) {
  const threads = [];

  // Transform stories into threads based on their type/content
  stories.forEach((story, index) => {
    const sharer = story.sharer || {
      firstName: user?.firstName || 'You',
      avatarColor: '#6B7F4A',
      initials: user?.avatarInitial || 'Y'
    };

    // Determine thread type based on story content
    let threadType = 'photo_voice'; // default
    if (story.hasVideo || story.mediaType === 'video') {
      threadType = 'video_wisdom';
    } else if (story.storyType === 'artifact' || story.title?.toLowerCase().includes('artifact')) {
      threadType = 'artifact';
    } else if (story.storyType === 'time_capsule') {
      threadType = 'time_capsule';
    }

    threads.push({
      id: `story-${story.id}`,
      threadType,
      question: story.title || 'A family story',
      preview: story.preview || '',
      date: story.date || null,
      location: story.location || null,
      rawDate: story.sharedAt || null,
      asker: {
        id: sharer.id || null,
        name: `${sharer.firstName || 'Family Member'}${sharer.lastName ? ` ${sharer.lastName}` : ''}`,
        initials: sharer.initials || getInitials(sharer.firstName, null),
        avatarColor: sharer.avatarColor || getAvatarColor(index),
        action: 'shared',
      },
      timeAgo: formatTimeAgo(story.sharedAt),
      mediaPreview: story.thumbnailUrl ? {
        thumbnail: getFullMediaUrl(story.thumbnailUrl),
        duration: story.duration || null,
      } : null,
      hasAudio: story.hasAudio || false,
      hasVideo: story.hasVideo || false,
      audioDuration: story.audioDuration || null,
      isWaiting: false,
      responses: story.responses || [],
      lastComment: story.lastComment ? {
        text: story.lastComment.text,
        authorName: story.lastComment.authorName,
        authorInitial: story.lastComment.authorInitial,
        timeAgo: formatTimeAgo(story.lastComment.timeAgo),
      } : null,
      engagement: story.engagement
        ? `${story.engagement.commentCount || 0} comments · ${story.engagement.reactionCount || 0} reactions`
        : null,
    });
  });

  // Transform questions into waiting threads
  questions.forEach((question, index) => {
    const asker = question.askedBy || connections[index % connections.length] || {};

    threads.push({
      id: `question-${question.id}`,
      threadType: 'photo_voice', // Questions are typically photo + voice responses
      question: question.text || question.question || 'What can you tell us about this?',
      rawDate: question.askedAt || null,
      asker: {
        id: asker.id || null,
        name: `${asker.firstName || 'Family Member'}${asker.lastName ? ` ${asker.lastName}` : ''}`,
        initials: getInitials(asker.firstName, asker.lastName),
        avatarColor: asker.avatarColor || getAvatarColor(index),
        action: 'asked',
      },
      timeAgo: formatTimeAgo(question.askedAt),
      mediaPreview: question.mediaUrl ? {
        thumbnail: getFullMediaUrl(question.mediaUrl),
      } : null,
      isWaiting: true,
      responses: [],
      engagement: null,
    });
  });

  return threads;
}

/**
 * Transform questions array into waiting items format for WaitingCard
 */
function transformQuestionsToWaitingItems(questions, connections) {
  if (!questions || questions.length === 0) {
    return [];
  }

  return questions.map((question, index) => {
    const asker = question.askedBy || connections[index % connections.length] || {};

    return {
      id: question.id || `q-${index}`,
      asker: {
        id: asker.id || null,
        firstName: asker.firstName || 'Family Member',
        lastName: asker.lastName || '',
        relationship: asker.relationship || 'Family',
        avatarColor: asker.avatarColor || getAvatarColor(index),
        initials: getInitials(asker.firstName, asker.lastName)
      },
      timeAgo: formatTimeAgo(question.askedAt),
      content: question.content || { type: 'text' },
      question: question.text || question.question || 'What would you like to share?',
      actionLabel: question.actionLabel || 'Respond'
    };
  });
}

/**
 * Transform stories array into recent moments format for RecentCard
 * Stories now include sharer info from the API (can be current user or family members)
 */
function transformStoriesToMoments(stories, user) {
  if (!stories || stories.length === 0) {
    return [];
  }

  return stories.map((story, index) => {
    // Use sharer info from API if available, otherwise fallback to user
    const sharer = story.sharer || {
      firstName: user?.firstName || 'You',
      relationship: 'You',
      avatarColor: '#6B7F4A',
      initials: user?.avatarInitial || 'Y'
    };

    return {
      id: story.id || `s-${index}`,
      sharer: {
        id: sharer.id || null,
        firstName: sharer.firstName || 'Family Member',
        lastName: sharer.lastName || '',
        relationship: sharer.relationship || 'Family',
        avatarColor: sharer.avatarColor || getAvatarColor(index),
        initials: sharer.initials || getInitials(sharer.firstName, null)
      },
      timeAgo: formatTimeAgo(story.sharedAt),
      title: story.title || 'Untitled Story',
      preview: story.preview || story.description || '',
      date: story.date || null,
      location: story.location || null,
      thumbnailUrl: getFullMediaUrl(story.thumbnailUrl),
      hasAudio: story.hasAudio || false,
      hasVideo: story.hasVideo || false,
      audioDuration: story.audioDuration || null,
      lastComment: story.lastComment ? {
        text: story.lastComment.text,
        authorName: story.lastComment.authorName,
        authorInitial: story.lastComment.authorInitial,
        timeAgo: formatTimeAgo(story.lastComment.timeAgo),
      } : null,
      reactions: story.engagement ? {
        count: (story.engagement.commentCount || 0) + (story.engagement.reactionCount || 0),
        names: (story.engagement.readBy || []).slice(0, 3).map(r => r.firstName)
      } : { count: 0, names: [] },
      actionLabel: 'View & Comment'
    };
  });
}

/**
 * Format a date/timestamp into a human-readable "time ago" string
 */
function formatTimeAgo(dateInput) {
  if (!dateInput) return 'Recently';

  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) !== 1 ? 's' : ''} ago`;

  return date.toLocaleDateString();
}

/**
 * Get initials from first and last name
 */
function getInitials(firstName, lastName) {
  const first = firstName ? firstName.charAt(0).toUpperCase() : '';
  const last = lastName ? lastName.charAt(0).toUpperCase() : '';
  return first + last || '?';
}

/**
 * Get a consistent avatar color based on index
 */
function getAvatarColor(index) {
  const colors = [
    '#6B7F4A', // Olive green
    '#4A5D2F', // Dark olive
    '#C65D34', // Terra cotta
    '#8B6914', // Golden brown
    '#5B7B8C', // Steel blue
    '#7B5B8C', // Purple
    '#8C5B6B', // Rose
    '#5B8C7B'  // Teal
  ];
  return colors[index % colors.length];
}

export default useFamilyBoardData;
